/**
 * Owner / Admin module and reports — port of `features/owner/owner_strings.dart`.
 * Keys and wording are kept identical to the Flutter table so the two stay in step;
 * Telugu entries are optional per key (fallback: English).
 */
import { l10nTable } from './l10n';

export const ownerStrings = l10nTable(
    {
        'moduleTitle': 'Owner / Admin module and reports',
        // common
        'cancel': 'Cancel',
        'save': 'Save',
        'done': 'Done',
        'retry': 'Retry',
        'all': 'All',
        'syncRequested': 'Sync requested — the hub pulls changes when it is online',
        'csvCopied': 'CSV copied to the clipboard',
        'exportCsv': 'Export CSV',
        'exportPdf': 'Export PDF',
        'allReports': 'All reports',
        'openReports': 'Open reports',
        'noData': 'No data for this period',
        'noDataBody': 'Cloud reports are as of the hub’s last sync. Try a longer range.',
        'pdfNotAvailable': 'PDF export needs a file-share plugin — CSV was copied instead',
        // filters
        'presetToday': 'Today',
        'presetYesterday': 'Yesterday',
        'preset7': '7 days',
        'preset30': '30 days',
        'preset90': '90 days',
        'presetMonth': 'This month',
        'presetCustom': 'Custom',
        'compare': 'vs previous',
        'filters': 'Filters',
        'filtersN': 'Filters · {n}',
        'channel': 'Channel',
        'dineIn': 'Dine-in',
        'takeaway': 'Takeaway',
        'guestType': 'Guest type',
        'hotelGuest': 'Hotel guest',
        'walkIn': 'Walk-in',
        // home
        'homeTitle': 'Owner · Dashboard',
        'homeSubCloud': '{outlet} · cloud',
        'kSalesToday': 'Sales today',
        'kCovers': 'Covers',
        'kOpenTables': 'Open tables',
        'kAvgKot': 'Avg KOT time',
        'kCash': 'Cash in drawer',
        'kFoodCost': 'Food cost',
        'subAvgBill': 'avg bill {v}',
        'subPendingKots': '{n} pending KOTs',
        'subInvoices': '{n} bills',
        'subRoomPending': '{n} room charges to post',
        'subFoodCostOf': 'of {v} net sales',
        'subTarget': 'target 30%',
        'topItemsToday': 'Top items today',
        'byQty': 'by qty',
        'noTopItems': 'No bills yet today',
        'lMenu': 'Menu & items',
        'lPricing': 'Pricing lists',
        'lSlots': 'Dining slots',
        'lCategories': 'Menu categories & slots',
        'catTitle': 'Menu categories',
        'catSub': '{n} categories',
        'catNone': 'No categories yet',
        'catAdd': 'Add category',
        'catEdit': 'Edit category',
        'catSaved': 'Category saved',
        'catOrder': 'Display order',
        'catEverySlot': 'All day — every slot',
        'catEverySlotHint': 'On the waiter menu in every slot — drinks, juices, desserts.',
        'catOnlyIn': 'Only in {slots}',
        'catServedIn': 'Served in',
        'catPickSlot': 'Pick at least one slot. For drinks and other all-day items, tap “All day”.',
        'catUnmapped': 'No slots picked — shows in every slot until you set it',
        'catNeedSlots': 'Add a dining slot first. A category has to be served in at least one.',
        'catNote': 'Waiters see a category only while one of its slots is open. Pick every slot for the ones that run all day.',
        'catChangesAtHub': 'Changes reach the hub at its next sync.',
        'lUsers': 'Users & devices',
        'lReports': 'Reports',
        'lReserve': 'Reservations',
        'lSalary': 'Salary & payroll',
        'lExpenses': 'Expenses',
        'lExpenseTypes': 'Expense types',
        'lAttendance': 'Attendance',
        'noteStaged': '{n} price edits are staged — effective from next business day',
        'notePending': 'Menu changes are pending at hub — the hub has not pulled them yet',
        'noteAllSynced': 'Hub is up to date with every menu and price change.',
        'selectOutlet': 'Choose an outlet',
        'selectOutletBody': 'Your account owns more than one outlet. Pick the one to manage.',
        'noOutlet': 'No outlet on this account',
        'signOut': 'Sign out',
        // menu
        'menuTitle': 'Menu',
        'menuSub': '{n} items · {c} categories',
        'searchItems': 'Search items',
        'stagedBanner': '{n} price edits staged — effective from next business day',
        'pendingBanner': 'changes pending at hub',
        'presetsTitle': 'Serving-list presets',
        'presetsBody': 'Managers apply these at slot open. Default approx. quantities pre-fill that screen.',
        'presetsNoApi': 'Presets are not in the cloud API yet — managers build the serving list on the hub at slot open.',
        'csvImport': 'CSV import',
        'csvImportBody': 'Copy the menu CSV to the clipboard, then import. Existing items are matched by short code.',
        'csvImportDone': 'Imported: {summary}',
        'csvClipboardEmpty': 'The clipboard has no CSV text',
        'newItem': 'New item',
        'editItem': 'Edit item',
        'fName': 'Name',
        'fNameTe': 'Regional-language name',
        'fShortCode': 'Short code',
        'fPrice': 'Dine-in price (₹)',
        'fTakeaway': 'Takeaway price (₹)',
        'fGst': 'GST rate (%)',
        'fCategory': 'Category',
        'veg': 'Veg',
        'nonVeg': 'Non-veg',
        'egg': 'Egg',
        'available': 'Available',
        'soldOut': 'Sold out',
        'activeFlag': 'Active',
        'priceStagedNote': 'Price and tax edits are staged — effective from next business day.',
        'itemSaved': 'Item saved',
        'noItems': 'No items match',
        'inactive': 'inactive',
        // pricing
        'pricingTitle': 'Pricing lists',
        'pricingSub': '{n} staged changes',
        'baseList': 'Dine-in',
        'takeawayList': 'Takeaway',
        'stagedTray': 'Staged changes — apply tomorrow',
        'changedBy': 'changed by {who}',
        'noStaged': 'Nothing staged. Price edits you make here apply from the next business day.',
        'colItem': 'Item',
        'colDineIn': 'Dine-in',
        'colTakeaway': 'Takeaway',
        'colPrice': 'Price',
        'pricingNote': 'Price and tax edits are always staged — effective from next business day, so an open slot never changes mid-service.',
        'saveStaged': 'Save as staged',
        'editPrice': 'New price · {item}',
        'priceStaged': 'Staged for the next business day',
        'cancelStaged': 'Cancel staged change',
        'cancelStagedBody': '{item}: {change} will not be applied.',
        // slots
        'slotsTitle': 'Dining slots',
        'slotsSub': '{n} slots',
        'slotOn': 'Active',
        'slotOff': 'Off',
        'priceListLine': 'Price list: {list} · token reset per slot',
        'allDaySlot': 'All-day slot',
        'allDayBody': 'One continuous slot instead of scheduled ones — for outlets that never close between services.',
        'slotCats': 'Categories served in this slot',
        'slotCatsHint': 'Untick one to stop serving it in this slot. It keeps its other slots.',
        'slotCatsUnmapped': '{n} categories have no slots picked yet, so they show in every slot. Set them in Menu → Categories.',
        'slotCatLast': '{name} would be left with no slot. Give it another slot in Menu → Categories first.',
        'slotsNote': 'Scheduled times are for reporting — they never block orders.',
        'addSlot': 'Add slot',
        'editSlot': 'Edit slot',
        'fStart': 'Start (HH:mm)',
        'fEnd': 'End (HH:mm)',
        'fDays': 'Days',
        'fPriceList': 'Price list',
        'noPriceList': 'Default',
        'deactivate': 'Deactivate',
        'slotSaved': 'Slot saved',
        'noSlots': 'No dining slots yet',
        // users
        'usersTitle': 'Users & devices',
        'usersSub': '{u} users · {d} devices',
        'managersOnlyTitle': 'Owner creates managers only',
        'managersOnlyBody': 'Waiter, cashier and kitchen (KDS) logins are created by the manager on the hub — they must work with no internet. Every PIN can be looked up here.',
        'addManager': 'Add manager',
        'users': 'Users',
        'devices': 'Devices',
        'stateActive': 'Active',
        'stateDisabled': 'Disabled',
        'originCloud': 'created in cloud by owner',
        'originHub': 'created on hub by manager',
        'resetPin': 'Reset PIN',
        'disable': 'Disable',
        'enable': 'Enable',
        'resetPinDone': 'New PIN for {name}: {pin} — works at the hub after its next sync',
        'pin': 'PIN',
        'show': 'Show',
        'hide': 'Hide',
        'roleCap': 'Role cap: {cap}',
        'role_OWNER': 'Owner',
        'role_MANAGER': 'Manager',
        'role_CASHIER': 'Cashier',
        'role_WAITER': 'Waiter',
        'role_KITCHEN': 'Kitchen (KDS)',
        'allRoles': 'All roles',
        'rotateKey': 'Rotate key',
        'revoke': 'Revoke',
        'revokeTitle': 'Revoke {name}?',
        'revokeBody': 'The device stops working at the hub’s next sync. It must be enrolled again with the QR and enrolment PIN.',
        'rotateRequested': 'Key rotation requested — the device re-scans the hub QR',
        'enrolledOn': 'enrolled {day}',
        'deviceState': '{state}',
        'enrolmentPin': 'Enrolment PIN',
        'pinSet': '• • • • • •',
        'pinNotSet': 'not set',
        'change': 'Change',
        'newEnrolmentPin': 'New enrolment PIN (6–8 digits)',
        'pinChanged': 'Enrolment PIN changed — reaches the hub at its next sync',
        'fMobile': 'Mobile number',
        'alsoCashier': 'Also cashier',
        'managerCreated': '{name} added — user code {code}, PIN {pin}',
        'noDevices': 'No devices enrolled yet',
        // reservations
        'reserveTitle': 'Reservations',
        'reserveSub': '{day} · {n} booked',
        'capacityTitle': '{day} · capacity',
        'capacitySub': '{n} tables',
        'tablesBooked': '{b} / {t} tables',
        'reservations': 'Reservations',
        'waitlist': 'Waitlist',
        'statusFlow': 'Booked → Confirmed → Seated → Completed / No-show / Cancelled. Linking an InnPro room booking pre-fills name and room.',
        'notify': 'Notify',
        'seat': 'Seat',
        'confirm': 'Confirm',
        'complete': 'Complete',
        'noShow': 'No-show',
        'cancelBooking': 'Cancel booking',
        'newReservation': 'New reservation',
        'fPhone': 'Phone',
        'fPax': 'Pax',
        'fTime': 'Time (HH:mm)',
        'fNotes': 'Notes',
        'fRoom': 'Room number (hotel guest)',
        'waitMinutes': '~{n} min',
        'noReservations': 'No reservations for this day',
        'noWaitlist': 'Nobody waiting',
        'pax': '{n} pax',
        'addToWaitlist': 'Add to waitlist',
        'st_BOOKED': 'Booked',
        'st_CONFIRMED': 'Confirmed',
        'st_SEATED': 'Seated',
        'st_COMPLETED': 'Completed',
        'st_NO_SHOW': 'No-show',
        'st_CANCELLED': 'Cancelled',
        // report picker
        'reportsTitle': 'Reports',
        'reportsSub': 'cloud · sync {time}',
        'itemCuisineSales': 'Item & cuisine sales',
        'vsPrev': 'vs prev {delta}',
        'qtyGrossNet': 'qty · gross · net — tap a row to drill cuisine → item → variant → orders',
        'prepVsSold': 'Prepared vs sold vs leftover',
        'overPrepared': 'Consistently over-prepared: {items} — cut tomorrow’s quantity by ~25%.',
        'noOverPrepared': 'Nothing is consistently over-prepared in this period.',
        'heatmap': 'Day × hour · net sales',
        'drillTitle': 'Orders · {name}',
        'group_ITEM': 'Items',
        'group_CATEGORY': 'Categories',
        'group_CUISINE': 'Cuisines',
        'group_VARIANT': 'Variants',
        'rpSales': 'Sales & money',
        'rpItems': 'Item consumption',
        'rpProfit': 'Profit analysis',
        'rpTax': 'Tax · GST by rate, B2B',
        'rpKot': 'KOT timing',
        'rpTables': 'Table utilisation',
        'rpStaff': 'Staff performance',
        'rpWaste': 'Wastage & leftover',
        'rpPay': 'Payment mix',
        'rpRoom': 'Room-charge reconciliation',
        // rpSales
        'salesTitle': 'Sales & money',
        'kNetSales': 'Net sales',
        'kPrevious': 'Previous period',
        'kAvgBill': 'Avg bill',
        'kDiscounts': 'Discounts given',
        'kVoids': 'Voids after KOT',
        'ofGross': '{p} of gross',
        'netLast6': 'Net sales · last 6 months',
        'salesBySlot': 'Sales by slot',
        'salesByChannel': 'Sales by channel',
        // rpItems
        'itemsTitle': 'Item consumption',
        'consumptionTitle': 'Menu item consumption · this vs previous',
        'colNow': 'Now',
        'colPrev': 'Prev',
        'colChange': 'Change',
        'byCuisine': 'Consumption by cuisine',
        'movers': 'Top & bottom movers',
        'zeroSales': 'Zero sales — candidates to remove',
        'itemInsight': '{down} down {dp} while {up} is up {upct}.',
        'hotelVsWalkIn': 'Hotel guest {h} · walk-in {w}',
        // rpProfit
        'profitTitle': 'Profit analysis',
        'kGrossMargin': 'Gross margin',
        'kNetProfit': 'Net profit',
        'kExpenses': 'Expenses',
        'ofSales': '{p} of sales',
        'pnl': 'P&L',
        'pnlNote': 'Expense lines come from what managers and the owner log — see Expenses.',
        'marginPerItem': 'Margin per item',
        'colCost': 'Cost',
        'colMargin': 'Mgn',
        'marginNote': 'Costs come from recipe & inventory. Items below 50% margin are flagged.',
        'noRecipes': 'No recipe costs yet — margins appear once recipes are maintained.',
        'sec_REVENUE': 'Revenue',
        'sec_COST_OF_SALES': 'Cost of sales',
        'sec_EXPENSES': 'Expenses',
        'sec_SALARIES': 'Salaries',
        'sec_PROFIT': 'Profit',
        // rpTax
        'taxTitle': 'Tax · GST',
        'kGstCollected': 'Total GST collected',
        'kB2b': 'B2B invoices',
        'kCreditNotes': 'Credit notes',
        'cgstSgst': 'CGST {c} · SGST {s}',
        'withGstin': '{v} with GSTIN',
        'gstByRate': 'GST by rate',
        'colRate': 'Rate',
        'colTaxable': 'Taxable',
        'colTax': 'Tax',
        'gstr1': 'GSTR-1 export',
        'invoiceRegister': 'B2B register',
        'rateLabel': 'GST {r}',
        // rpKot
        'kotTitle': 'KOT timing',
        'kotPerStation': 'Average KOT time per station',
        'slaSuffix': '{p} SLA',
        'slowestHours': 'Slowest hours',
        'kotInsight': '{station} breaches the 20-minute SLA in {p} of tickets — the slowest station this period.',
        // rpTables
        'tablesTitle': 'Table utilisation',
        'turnsTitle': 'Turns & seated time per table',
        'colTable': 'Table',
        'colAvgSeated': 'Avg seated',
        'colTurns': 'Turns',
        'colCovers': 'Covers',
        'peakOccupancy': 'Peak occupancy by hour',
        'ordersOpened': '{n} orders',
        // rpStaff
        'staffTitle': 'Staff performance',
        'perStaff': 'Per captain',
        'staffLine': '{inv} bills · avg bill {avg} · {cov} covers',
        'discountsVoids': 'Discounts & voids by staff',
        'discountLine': 'Discount · {ref}',
        'voidLine': 'Void · {ref}',
        'byWho': '{user} · {reason}',
        'approvedBy': ' · approved {who}',
        'noDiscountsVoids': 'No discounts or voids in this period',
        // rpWaste
        'wasteTitle': 'Wastage & leftover',
        'periodSummary': 'Period summary',
        'wPrepared': 'Portions prepared',
        'wSold': 'Sold',
        'wLeftover': 'Left over',
        'wSellThrough': 'Sell-through',
        'wPending': 'Lines awaiting verification',
        'prepSoldWasted': 'Prepared vs sold vs left over',
        'colPrep': 'Prep',
        'colSold': 'Sold',
        'colLeft': 'Left',
        'colPct': '%',
        'wasteInsight': '{items} left the most food behind — cut their standing prep and let the serving list carry forward instead.',
        // rpPay
        'payTitle': 'Payment mix',
        'collectionsByMode': 'Collections by mode',
        'cashByCashier': 'Cash collected by cashier',
        'cashNote': 'Expected vs counted variance per cash-up is on each day’s Z report.',
        'mode_CASH': 'Cash',
        'mode_UPI': 'UPI',
        'mode_CARD': 'Card',
        'mode_WALLET': 'Wallet',
        'mode_OTHER': 'Other',
        'mode_ROOM_CHARGE': 'Room charge',
        // rpRoom
        'roomTitle': 'Room-charge reconciliation',
        'kPosted': 'Posted to folios',
        'kPendingPost': 'Pending',
        'kRejected': 'Rejected / failed',
        'postings': '{n} postings',
        'recentPostings': 'Recent postings',
        'roomNote': 'Matched against InnPro folios each night. Rejected postings need a manual settlement in the restaurant.',
        'retryPosting': 'Retry',
        'retryQueued': 'Posting queued for retry',
        'rs_POSTED': 'Posted',
        'rs_PENDING': 'Pending sync',
        'rs_FAILED': 'Failed',
        'rs_REVERSED': 'Reversed',
        'rs_BLOCKED': 'Rejected · checked out',
        // salary
        'salaryTitle': 'Salary & payroll',
        'salarySub': '{month} · {n} staff · {total}',
        'kPayroll': 'Payroll this month',
        'kPaid': 'Paid',
        'kPendingPay': 'Pending',
        'kAdvances': 'Advances outstanding',
        'nStaff': '{n} staff',
        'payrollFor': '{month} payroll',
        'shiftsAdvance': '{days} shifts · advance {adv}',
        'statePaid': 'Paid',
        'statePending': 'Pending',
        'stateDraft': 'Draft',
        'salaryNote': 'Days come from the manager’s attendance log. Salaries post to the P&L as “Salaries & wages” in the month they are paid.',
        'logAdvance': 'Log advance',
        'markPaid': 'Mark salaries paid',
        'logExpense': 'Log another expense',
        'noPayroll': 'No payroll for {month} yet',
        'noPayrollBody': 'Create it to calculate pay from attendance and day wages.',
        'createPayroll': 'Create {month} payroll',
        'markPaidTitle': 'Mark {n} salaries paid?',
        'markPaidBody': 'Finalizes the payroll and records each unpaid line as paid by {mode}. Salaries post to expenses.',
        'paidDone': 'Salaries marked paid',
        'fStaff': 'Staff member',
        'fAmount': 'Amount (₹)',
        'fMode': 'Paid by',
        'advanceSaved': 'Advance recorded — deducted from the next pay',
        'pm_CASH': 'Cash',
        'pm_UPI': 'UPI',
        'pm_BANK': 'Bank transfer',
        'pm_CARD': 'Card',
        'pm_CHEQUE': 'Cheque',
        'pm_OTHER': 'Other',
        // expense types
        'expenseTypesTitle': 'Expense types',
        'expenseTypesSub': 'lookup master · {n} types',
        'expenseTypesIntro': 'The lookup every expense entry picks from. Tap a type to control who can log against it — {visible}.',
        'visibleCount': '{v} of {t} types visible to managers',
        'mtd': 'MTD {v}',
        'managersCanLog': 'Managers can log',
        'ownerOnly': 'Owner only',
        'inactiveType': 'Inactive',
        'expenseTypesNote': 'Each type maps to a P&L line. Owner only types — rent, salaries, licences — never appear in the manager’s Log expense chips, so daily market spend stays separate from fixed costs.',
        'addExpenseType': 'Add expense type',
        'typeName': 'Type name',
        'typeSaved': 'Expense type saved',
        'noExpenseTypes': 'No expense types yet',
    },
    {
        'moduleTitle': 'యజమాని / అడ్మిన్, రిపోర్టులు',
        // common
        'cancel': 'వద్దు',
        'save': 'సేవ్',
        'done': 'అయింది',
        'retry': 'మళ్లీ ప్రయత్నించండి',
        'all': 'అన్నీ',
        'syncRequested': 'సింక్ అడిగాం — హబ్ ఆన్‌లైన్ అయినప్పుడు మార్పులు తీసుకుంటుంది',
        'csvCopied': 'CSV కాపీ అయింది',
        'exportCsv': 'CSV తీసుకోండి',
        'exportPdf': 'PDF తీసుకోండి',
        'allReports': 'అన్ని రిపోర్టులు',
        'openReports': 'రిపోర్టులు తెరవండి',
        'noData': 'ఈ రోజులకు వివరాలు లేవు',
        'noDataBody': 'క్లౌడ్ రిపోర్టులు హబ్ చివరిసారి సింక్ అయినప్పటి వరకే ఉంటాయి. ఎక్కువ రోజులు ఎంచుకోండి.',
        'pdfNotAvailable': 'PDF ఇంకా రాదు — బదులుగా CSV కాపీ అయింది',
        // filters
        'presetToday': 'ఈరోజు',
        'presetYesterday': 'నిన్న',
        'preset7': '7 రోజులు',
        'preset30': '30 రోజులు',
        'preset90': '90 రోజులు',
        'presetMonth': 'ఈ నెల',
        'presetCustom': 'తేదీలు ఎంచుకోండి',
        'compare': 'ముందు రోజులతో పోల్చి',
        'filters': 'ఫిల్టర్లు',
        'filtersN': 'ఫిల్టర్లు · {n}',
        'channel': 'ఆర్డర్ రకం',
        'dineIn': 'డైన్-ఇన్',
        'takeaway': 'టేక్‌అవే',
        'guestType': 'అతిథి రకం',
        'hotelGuest': 'హోటల్ అతిథి',
        'walkIn': 'బయటి వారు',
        // home
        'homeTitle': 'యజమాని · డాష్‌బోర్డ్',
        'homeSubCloud': '{outlet} · క్లౌడ్',
        'kSalesToday': 'ఈరోజు అమ్మకాలు',
        'kCovers': 'మంది',
        'kOpenTables': 'తెరిచి ఉన్న టేబుళ్లు',
        'kAvgKot': 'సగటు KOT సమయం',
        'kCash': 'డ్రాయర్‌లో క్యాష్',
        'kFoodCost': 'ఆహారం ఖర్చు',
        'subAvgBill': 'సగటు బిల్లు {v}',
        'subPendingKots': '{n} KOTలు ఇంకా ఉన్నాయి',
        'subInvoices': '{n} బిల్లులు',
        'subRoomPending': '{n} రూమ్ ఛార్జ్‌లు పంపాలి',
        'subFoodCostOf': 'నికర అమ్మకాలు {v} లో',
        'subTarget': 'లక్ష్యం 30%',
        'topItemsToday': 'ఈరోజు ఎక్కువ అమ్మిన వస్తువులు',
        'byQty': 'సంఖ్య ప్రకారం',
        'noTopItems': 'ఈరోజు ఇంకా బిల్లులు లేవు',
        'lMenu': 'మెనూ, వస్తువులు',
        'lPricing': 'ధరల లిస్ట్‌లు',
        'lSlots': 'స్లాట్‌లు',
        'lCategories': 'మెనూ కేటగిరీలు, స్లాట్‌లు',
        'catTitle': 'మెనూ కేటగిరీలు',
        'catSub': '{n} కేటగిరీలు',
        'catNone': 'ఇంకా కేటగిరీలు లేవు',
        'catAdd': 'కేటగిరీ జోడించండి',
        'catEdit': 'కేటగిరీ మార్చండి',
        'catSaved': 'కేటగిరీ సేవ్ అయింది',
        'catOrder': 'చూపించే వరుస',
        'catEverySlot': 'రోజంతా — ప్రతి స్లాట్‌లో',
        'catEverySlotHint': 'వెయిటర్ మెనూలో ప్రతి స్లాట్‌లో కనిపిస్తుంది — డ్రింక్స్, జ్యూస్‌లు, స్వీట్లు.',
        'catOnlyIn': '{slots} లో మాత్రమే',
        'catServedIn': 'ఏ స్లాట్‌లో సర్వ్ చేస్తారు',
        'catPickSlot': 'కనీసం ఒక స్లాట్ ఎంచుకోండి. డ్రింక్స్ లాంటి రోజంతా ఉండే వస్తువులకు “రోజంతా” నొక్కండి.',
        'catUnmapped': 'స్లాట్ ఎంచుకోలేదు — మీరు ఎంచుకునే వరకు ప్రతి స్లాట్‌లో కనిపిస్తుంది',
        'catNeedSlots': 'ముందు ఒక స్లాట్ జోడించండి. కేటగిరీకి కనీసం ఒక స్లాట్ ఉండాలి.',
        'catNote': 'కేటగిరీ దాని స్లాట్ తెరిచి ఉన్నప్పుడే వెయిటర్‌లకు కనిపిస్తుంది. రోజంతా ఉండే వాటికి అన్ని స్లాట్‌లు ఎంచుకోండి.',
        'catChangesAtHub': 'మార్పులు తర్వాతి సింక్‌లో హబ్‌కు చేరతాయి.',
        'lUsers': 'యూజర్లు, ఫోన్లు',
        'lReports': 'రిపోర్టులు',
        'lReserve': 'రిజర్వేషన్లు',
        'lSalary': 'జీతాలు',
        'lExpenses': 'చెల్లించిన డబ్బు',
        'lExpenseTypes': 'చెల్లించిన డబ్బు రకాలు',
        'lAttendance': 'హాజరు',
        'noteStaged': '{n} ధర మార్పులు వేచి ఉన్నాయి — తర్వాతి పని రోజు నుంచి మారతాయి',
        'notePending': 'మెనూ మార్పులు ఇంకా హబ్‌కు చేరలేదు — హబ్ ఇంకా తీసుకోలేదు',
        'noteAllSynced': 'మెనూ, ధర మార్పులన్నీ హబ్‌కు చేరాయి.',
        'selectOutlet': 'అవుట్‌లెట్ ఎంచుకోండి',
        'selectOutletBody': 'మీ అకౌంట్‌లో ఒకటి కంటే ఎక్కువ అవుట్‌లెట్‌లు ఉన్నాయి. ఏది చూడాలో ఎంచుకోండి.',
        'noOutlet': 'ఈ అకౌంట్‌లో అవుట్‌లెట్ లేదు',
        'signOut': 'సైన్ అవుట్',
        // menu
        'menuTitle': 'మెనూ',
        'menuSub': '{n} వస్తువులు · {c} కేటగిరీలు',
        'searchItems': 'వస్తువులు వెతకండి',
        'stagedBanner': '{n} ధర మార్పులు వేచి ఉన్నాయి — తర్వాతి పని రోజు నుంచి మారతాయి',
        'pendingBanner': 'మార్పులు ఇంకా హబ్‌కు చేరలేదు',
        'presetsTitle': 'ముందే తయారు చేసిన సర్వింగ్ లిస్ట్‌లు',
        'presetsBody': 'స్లాట్ తెరిచేటప్పుడు మేనేజర్ వీటిని వాడతారు. సుమారు సంఖ్యలు ఆ స్క్రీన్‌లో ముందే నిండి ఉంటాయి.',
        'presetsNoApi': 'ఇవి క్లౌడ్‌లో ఇంకా లేవు — స్లాట్ తెరిచేటప్పుడు మేనేజర్ హబ్‌లో సర్వింగ్ లిస్ట్ తయారు చేస్తారు.',
        'csvImport': 'CSV ఇంపోర్ట్',
        'csvImportBody': 'మెనూ CSV ని కాపీ చేసి, తర్వాత ఇంపోర్ట్ నొక్కండి. ముందే ఉన్న వస్తువులను షార్ట్ కోడ్‌తో గుర్తిస్తుంది.',
        'csvImportDone': 'ఇంపోర్ట్ అయింది: {summary}',
        'csvClipboardEmpty': 'కాపీ చేసిన CSV ఏమీ లేదు',
        'newItem': 'కొత్త వస్తువు',
        'editItem': 'వస్తువు మార్చండి',
        'fName': 'పేరు',
        'fNameTe': 'మీ భాషలో పేరు',
        'fShortCode': 'షార్ట్ కోడ్',
        'fPrice': 'డైన్-ఇన్ ధర (₹)',
        'fTakeaway': 'టేక్‌అవే ధర (₹)',
        'fGst': 'GST రేటు (%)',
        'fCategory': 'కేటగిరీ',
        'veg': 'వెజ్',
        'nonVeg': 'నాన్-వెజ్',
        'egg': 'గుడ్డు',
        'available': 'ఉంది',
        'soldOut': 'అయిపోయింది',
        'activeFlag': 'వాడుకలో ఉంది',
        'priceStagedNote': 'ధర, పన్ను మార్పులు వెంటనే మారవు — తర్వాతి పని రోజు నుంచి మారతాయి.',
        'itemSaved': 'వస్తువు సేవ్ అయింది',
        'noItems': 'ఏ వస్తువూ దొరకలేదు',
        'inactive': 'వాడుకలో లేదు',
        // pricing
        'pricingTitle': 'ధరల లిస్ట్‌లు',
        'pricingSub': '{n} మార్పులు వేచి ఉన్నాయి',
        'baseList': 'డైన్-ఇన్',
        'takeawayList': 'టేక్‌అవే',
        'stagedTray': 'వేచి ఉన్న మార్పులు — రేపటి నుంచి',
        'changedBy': 'మార్చినవారు: {who}',
        'noStaged': 'వేచి ఉన్న మార్పులు లేవు. ఇక్కడ మార్చిన ధరలు తర్వాతి పని రోజు నుంచి మారతాయి.',
        'colItem': 'వస్తువు',
        'colDineIn': 'డైన్-ఇన్',
        'colTakeaway': 'టేక్‌అవే',
        'colPrice': 'ధర',
        'pricingNote': 'ధర, పన్ను మార్పులు ఎప్పుడూ వెంటనే మారవు — తర్వాతి పని రోజు నుంచి మారతాయి. అందుకే తెరిచి ఉన్న స్లాట్‌లో ధర మధ్యలో మారదు.',
        'saveStaged': 'తర్వాతి రోజుకు సేవ్ చేయండి',
        'editPrice': 'కొత్త ధర · {item}',
        'priceStaged': 'తర్వాతి పని రోజు నుంచి మారుతుంది',
        'cancelStaged': 'వేచి ఉన్న మార్పు రద్దు చేయండి',
        'cancelStagedBody': '{item}: {change} మారదు.',
        // slots
        'slotsTitle': 'స్లాట్‌లు',
        'slotsSub': '{n} స్లాట్‌లు',
        'slotOn': 'ఆన్',
        'slotOff': 'ఆఫ్',
        'priceListLine': 'ధరల లిస్ట్: {list} · ప్రతి స్లాట్‌కు టోకెన్ నంబర్ మళ్లీ మొదలవుతుంది',
        'allDaySlot': 'రోజంతా ఒకే స్లాట్',
        'allDayBody': 'వేర్వేరు స్లాట్‌లకు బదులు ఒకే స్లాట్ — మధ్యలో మూయని అవుట్‌లెట్‌లకు.',
        'slotCats': 'ఈ స్లాట్‌లో సర్వ్ చేసే కేటగిరీలు',
        'slotCatsHint': 'ఈ స్లాట్‌లో వద్దనుకుంటే టిక్ తీసేయండి. మిగతా స్లాట్‌లలో అలాగే ఉంటుంది.',
        'slotCatsUnmapped': '{n} కేటగిరీలకు స్లాట్ ఎంచుకోలేదు, అందుకే ప్రతి స్లాట్‌లో కనిపిస్తాయి. మెనూ → కేటగిరీలు లో సెట్ చేయండి.',
        'slotCatLast': '{name} కు ఏ స్లాట్ మిగలదు. ముందు మెనూ → కేటగిరీలు లో దానికి ఇంకో స్లాట్ ఇవ్వండి.',
        'slotsNote': 'ఈ సమయాలు రిపోర్టుల కోసం మాత్రమే — ఆర్డర్లను ఎప్పుడూ ఆపవు.',
        'addSlot': 'స్లాట్ జోడించండి',
        'editSlot': 'స్లాట్ మార్చండి',
        'fStart': 'మొదలు (HH:mm)',
        'fEnd': 'ముగింపు (HH:mm)',
        'fDays': 'రోజులు',
        'fPriceList': 'ధరల లిస్ట్',
        'noPriceList': 'మామూలు',
        'deactivate': 'ఆపేయండి',
        'slotSaved': 'స్లాట్ సేవ్ అయింది',
        'noSlots': 'ఇంకా స్లాట్‌లు లేవు',
        // users
        'usersTitle': 'యూజర్లు, ఫోన్లు',
        'usersSub': '{u} యూజర్లు · {d} ఫోన్లు',
        'managersOnlyTitle': 'యజమాని మేనేజర్‌లను మాత్రమే జోడిస్తారు',
        'managersOnlyBody': 'వెయిటర్, క్యాషియర్, వంటగది (KDS) లాగిన్‌లను మేనేజర్ హబ్‌లో తయారు చేస్తారు — అవి ఇంటర్నెట్ లేకుండా పనిచేయాలి. ప్రతి PIN ఇక్కడ చూడవచ్చు.',
        'addManager': 'మేనేజర్‌ను జోడించండి',
        'users': 'యూజర్లు',
        'devices': 'ఫోన్లు',
        'stateActive': 'పనిచేస్తోంది',
        'stateDisabled': 'ఆపేశారు',
        'originCloud': 'యజమాని క్లౌడ్‌లో జోడించారు',
        'originHub': 'మేనేజర్ హబ్‌లో జోడించారు',
        'resetPin': 'కొత్త PIN ఇవ్వండి',
        'disable': 'ఆపేయండి',
        'enable': 'మళ్లీ ఆన్ చేయండి',
        'resetPinDone': '{name} కొత్త PIN: {pin} — తర్వాతి సింక్ అయ్యాక హబ్‌లో పనిచేస్తుంది',
        'pin': 'PIN',
        'show': 'చూపించండి',
        'hide': 'దాచండి',
        'roleCap': 'ఎక్కువలో ఎక్కువ రోల్: {cap}',
        'role_OWNER': 'యజమాని',
        'role_MANAGER': 'మేనేజర్',
        'role_CASHIER': 'క్యాషియర్',
        'role_WAITER': 'వెయిటర్',
        'role_KITCHEN': 'వంటగది (KDS)',
        'allRoles': 'అన్ని రోల్స్',
        'rotateKey': 'కీ మార్చండి',
        'revoke': 'తీసేయండి',
        'revokeTitle': '{name} ను తీసేయాలా?',
        'revokeBody': 'హబ్ తర్వాతి సింక్ అయ్యాక ఈ ఫోన్ పనిచేయదు. మళ్లీ వాడాలంటే QR, ఫోన్ చేర్చే PIN తో మళ్లీ చేర్చాలి.',
        'rotateRequested': 'కీ మార్చమని అడిగాం — ఆ ఫోన్‌లో హబ్ QR మళ్లీ స్కాన్ చేయాలి',
        'enrolledOn': '{day} న చేర్చారు',
        'deviceState': '{state}',
        'enrolmentPin': 'ఫోన్ చేర్చే PIN',
        'pinSet': '• • • • • •',
        'pinNotSet': 'పెట్టలేదు',
        'change': 'మార్చండి',
        'newEnrolmentPin': 'ఫోన్ చేర్చే కొత్త PIN (6–8 అంకెలు)',
        'pinChanged': 'ఫోన్ చేర్చే PIN మారింది — తర్వాతి సింక్‌లో హబ్‌కు చేరుతుంది',
        'fMobile': 'మొబైల్ నంబర్',
        'alsoCashier': 'క్యాషియర్ కూడా',
        'managerCreated': '{name} ను జోడించాం — యూజర్ కోడ్ {code}, PIN {pin}',
        'noDevices': 'ఇంకా ఏ ఫోన్ చేర్చలేదు',
        // reservations
        'reserveTitle': 'రిజర్వేషన్లు',
        'reserveSub': '{day} · {n} బుక్ అయ్యాయి',
        'capacityTitle': '{day} · టేబుళ్ల లెక్క',
        'capacitySub': '{n} టేబుళ్లు',
        'tablesBooked': '{b} / {t} టేబుళ్లు',
        'reservations': 'రిజర్వేషన్లు',
        'waitlist': 'వెయిటింగ్ లిస్ట్',
        'statusFlow': 'బుక్ అయింది → ఖాయం → కూర్చున్నారు → పూర్తయింది / రాలేదు / రద్దు. InnPro రూమ్ బుకింగ్ కలిపితే పేరు, రూమ్ నంబర్ వాటంతట అవే వస్తాయి.',
        'notify': 'తెలియజేయండి',
        'seat': 'కూర్చోబెట్టండి',
        'confirm': 'ఖాయం చేయండి',
        'complete': 'పూర్తి చేయండి',
        'noShow': 'రాలేదు',
        'cancelBooking': 'బుకింగ్ రద్దు చేయండి',
        'newReservation': 'కొత్త రిజర్వేషన్',
        'fPhone': 'ఫోన్',
        'fPax': 'ఎంత మంది',
        'fTime': 'సమయం (HH:mm)',
        'fNotes': 'గమనిక',
        'fRoom': 'రూమ్ నంబర్ (హోటల్ అతిథి)',
        'waitMinutes': '~{n} నిమి',
        'noReservations': 'ఈ రోజుకు రిజర్వేషన్లు లేవు',
        'noWaitlist': 'ఎవరూ వేచి లేరు',
        'pax': '{n} మంది',
        'addToWaitlist': 'వెయిటింగ్ లిస్ట్‌లో జోడించండి',
        'st_BOOKED': 'బుక్ అయింది',
        'st_CONFIRMED': 'ఖాయం',
        'st_SEATED': 'కూర్చున్నారు',
        'st_COMPLETED': 'పూర్తయింది',
        'st_NO_SHOW': 'రాలేదు',
        'st_CANCELLED': 'రద్దు',
        // report picker
        'reportsTitle': 'రిపోర్టులు',
        'reportsSub': 'క్లౌడ్ · సింక్ {time}',
        'itemCuisineSales': 'వస్తువు, వంటకాల రకం వారీగా అమ్మకాలు',
        'vsPrev': 'ముందుతో పోలిస్తే {delta}',
        'qtyGrossNet': 'సంఖ్య · మొత్తం · నికర — వరుస నొక్కితే వంటకాల రకం → వస్తువు → వేరియంట్ → ఆర్డర్లు కనిపిస్తాయి',
        'prepVsSold': 'వండినవి, అమ్మినవి, మిగిలినవి',
        'overPrepared': 'ఎప్పుడూ ఎక్కువ వండుతున్నవి: {items} — రేపు సుమారు 25% తక్కువ వండండి.',
        'noOverPrepared': 'ఈ రోజుల్లో ఏదీ ఎప్పుడూ ఎక్కువ వండలేదు.',
        'heatmap': 'రోజు × గంట · నికర అమ్మకాలు',
        'drillTitle': 'ఆర్డర్లు · {name}',
        'group_ITEM': 'వస్తువులు',
        'group_CATEGORY': 'కేటగిరీలు',
        'group_CUISINE': 'వంటకాల రకాలు',
        'group_VARIANT': 'వేరియంట్‌లు',
        'rpSales': 'అమ్మకాలు, డబ్బు',
        'rpItems': 'ఏ వస్తువు ఎంత అమ్ముడైంది',
        'rpProfit': 'లాభం లెక్క',
        'rpTax': 'పన్ను · రేటు వారీగా GST, B2B',
        'rpKot': 'KOT సమయం',
        'rpTables': 'టేబుళ్ల వాడకం',
        'rpStaff': 'సిబ్బంది పనితీరు',
        'rpWaste': 'వృథా, మిగిలినవి',
        'rpPay': 'డబ్బు ఏ పద్ధతిలో వచ్చింది',
        'rpRoom': 'రూమ్ ఛార్జ్ సరిచూడటం',
        // rpSales
        'salesTitle': 'అమ్మకాలు, డబ్బు',
        'kNetSales': 'నికర అమ్మకాలు',
        'kPrevious': 'ముందు రోజులు',
        'kAvgBill': 'సగటు బిల్లు',
        'kDiscounts': 'ఇచ్చిన డిస్కౌంట్‌లు',
        'kVoids': 'KOT తర్వాత రద్దు',
        'ofGross': 'మొత్తం అమ్మకాల్లో {p}',
        'netLast6': 'నికర అమ్మకాలు · గత 6 నెలలు',
        'salesBySlot': 'స్లాట్ వారీగా అమ్మకాలు',
        'salesByChannel': 'ఆర్డర్ రకం వారీగా అమ్మకాలు',
        // rpItems
        'itemsTitle': 'ఏ వస్తువు ఎంత అమ్ముడైంది',
        'consumptionTitle': 'మెనూ వస్తువుల అమ్మకం · ఇప్పుడు, ముందు',
        'colNow': 'ఇప్పుడు',
        'colPrev': 'ముందు',
        'colChange': 'మార్పు',
        'byCuisine': 'వంటకాల రకం వారీగా అమ్మకం',
        'movers': 'ఎక్కువ పెరిగినవి, తగ్గినవి',
        'zeroSales': 'అసలు అమ్ముడుకానివి — తీసేయవచ్చు',
        'itemInsight': '{down} {dp} తగ్గింది, {up} {upct} పెరిగింది.',
        'hotelVsWalkIn': 'హోటల్ అతిథి {h} · బయటి వారు {w}',
        // rpProfit
        'profitTitle': 'లాభం లెక్క',
        'kGrossMargin': 'మొత్తం లాభం',
        'kNetProfit': 'నికర లాభం',
        'kExpenses': 'ఖర్చులు',
        'ofSales': 'అమ్మకాల్లో {p}',
        'pnl': 'లాభ నష్టాలు',
        'pnlNote': 'ఖర్చుల వరుసలు మేనేజర్‌లు, యజమాని రాసిన వాటి నుంచి వస్తాయి — చెల్లించిన డబ్బు చూడండి.',
        'marginPerItem': 'వస్తువు వారీగా లాభం',
        'colCost': 'ఖర్చు',
        'colMargin': 'లాభం',
        'marginNote': 'ఖర్చులు రెసిపీ, స్టాక్ నుంచి వస్తాయి. 50% కంటే తక్కువ లాభం ఉన్న వస్తువులకు గుర్తు పెడతాం.',
        'noRecipes': 'ఇంకా రెసిపీ ఖర్చులు లేవు — రెసిపీలు రాస్తే లాభం కనిపిస్తుంది.',
        'sec_REVENUE': 'ఆదాయం',
        'sec_COST_OF_SALES': 'అమ్మిన వాటి ఖర్చు',
        'sec_EXPENSES': 'ఖర్చులు',
        'sec_SALARIES': 'జీతాలు',
        'sec_PROFIT': 'లాభం',
        // rpTax
        'taxTitle': 'పన్ను · GST',
        'kGstCollected': 'వసూలైన మొత్తం GST',
        'kB2b': 'B2B బిల్లులు',
        'kCreditNotes': 'క్రెడిట్ నోట్‌లు',
        'cgstSgst': 'CGST {c} · SGST {s}',
        'withGstin': 'GSTIN ఉన్నవి {v}',
        'gstByRate': 'రేటు వారీగా GST',
        'colRate': 'రేటు',
        'colTaxable': 'పన్ను పడే మొత్తం',
        'colTax': 'పన్ను',
        'gstr1': 'GSTR-1 తీసుకోండి',
        'invoiceRegister': 'B2B రిజిస్టర్',
        'rateLabel': 'GST {r}',
        // rpKot
        'kotTitle': 'KOT సమయం',
        'kotPerStation': 'స్టేషన్ వారీగా సగటు KOT సమయం',
        'slaSuffix': '{p} సమయానికి',
        'slowestHours': 'ఎక్కువ ఆలస్యమైన గంటలు',
        'kotInsight': '{station} లో {p} KOTలు 20 నిమిషాలు దాటాయి — ఈ రోజుల్లో అన్నిటికంటే నెమ్మది స్టేషన్.',
        // rpTables
        'tablesTitle': 'టేబుళ్ల వాడకం',
        'turnsTitle': 'టేబుల్ వారీగా ఎన్నిసార్లు నిండింది, ఎంతసేపు కూర్చున్నారు',
        'colTable': 'టేబుల్',
        'colAvgSeated': 'సగటు కూర్చున్న సమయం',
        'colTurns': 'ఎన్నిసార్లు',
        'colCovers': 'మంది',
        'peakOccupancy': 'గంట వారీగా ఎక్కువ రద్దీ',
        'ordersOpened': '{n} ఆర్డర్లు',
        // rpStaff
        'staffTitle': 'సిబ్బంది పనితీరు',
        'perStaff': 'కెప్టెన్ వారీగా',
        'staffLine': '{inv} బిల్లులు · సగటు బిల్లు {avg} · {cov} మంది',
        'discountsVoids': 'సిబ్బంది వారీగా డిస్కౌంట్‌లు, రద్దులు',
        'discountLine': 'డిస్కౌంట్ · {ref}',
        'voidLine': 'రద్దు · {ref}',
        'byWho': '{user} · {reason}',
        'approvedBy': ' · ఒప్పుకున్నవారు {who}',
        'noDiscountsVoids': 'ఈ రోజుల్లో డిస్కౌంట్‌లు, రద్దులు లేవు',
        // rpWaste
        'wasteTitle': 'వృథా, మిగిలినవి',
        'periodSummary': 'ఈ రోజుల మొత్తం లెక్క',
        'wPrepared': 'వండినవి',
        'wSold': 'అమ్మినవి',
        'wLeftover': 'మిగిలినవి',
        'wSellThrough': 'వండిన వాటిలో అమ్మినవి',
        'wPending': 'ఇంకా సరిచూడాల్సినవి',
        'prepSoldWasted': 'వండినవి, అమ్మినవి, మిగిలినవి',
        'colPrep': 'వండినవి',
        'colSold': 'అమ్మినవి',
        'colLeft': 'మిగిలినవి',
        'colPct': '%',
        'wasteInsight': '{items} ఎక్కువగా మిగిలాయి — వీటిని రోజూ తక్కువ వండండి, మిగిలినది సర్వింగ్ లిస్ట్‌లో తర్వాతి స్లాట్‌కు వెళ్లనివ్వండి.',
        // rpPay
        'payTitle': 'డబ్బు ఏ పద్ధతిలో వచ్చింది',
        'collectionsByMode': 'పద్ధతి వారీగా వచ్చిన డబ్బు',
        'cashByCashier': 'క్యాషియర్ వారీగా వచ్చిన క్యాష్',
        'cashNote': 'ఉండాల్సిన క్యాష్, లెక్కపెట్టిన క్యాష్ మధ్య తేడా ప్రతి రోజు Z రిపోర్ట్‌లో ఉంటుంది.',
        'mode_CASH': 'క్యాష్',
        'mode_UPI': 'UPI',
        'mode_CARD': 'కార్డ్',
        'mode_WALLET': 'వాలెట్',
        'mode_OTHER': 'ఇతర',
        'mode_ROOM_CHARGE': 'రూమ్ ఛార్జ్',
        // rpRoom
        'roomTitle': 'రూమ్ ఛార్జ్ సరిచూడటం',
        'kPosted': 'రూమ్ బిల్లుకు చేరినవి',
        'kPendingPost': 'ఇంకా చేరాలి',
        'kRejected': 'ఒప్పుకోలేదు / పంపలేకపోయాం',
        'postings': '{n} ఛార్జ్‌లు',
        'recentPostings': 'ఇటీవలి ఛార్జ్‌లు',
        'roomNote': 'ప్రతి రాత్రి InnPro రూమ్ బిల్లులతో సరిచూస్తాం. ఒప్పుకోని ఛార్జ్‌లకు రెస్టారెంట్‌లోనే డబ్బు తీసుకోవాలి.',
        'retryPosting': 'మళ్లీ ప్రయత్నించండి',
        'retryQueued': 'మళ్లీ పంపడానికి పెట్టాం',
        'rs_POSTED': 'చేరింది',
        'rs_PENDING': 'సింక్ కావాలి',
        'rs_FAILED': 'పంపలేకపోయాం',
        'rs_REVERSED': 'వెనక్కి తీసుకున్నారు',
        'rs_BLOCKED': 'ఒప్పుకోలేదు · చెక్ అవుట్ అయ్యారు',
        // salary
        'salaryTitle': 'జీతాలు',
        'salarySub': '{month} · {n} సిబ్బంది · {total}',
        'kPayroll': 'ఈ నెల జీతాలు',
        'kPaid': 'ఇచ్చినవి',
        'kPendingPay': 'ఇవ్వాల్సినవి',
        'kAdvances': 'తిరిగి రావాల్సిన అడ్వాన్స్‌లు',
        'nStaff': '{n} సిబ్బంది',
        'payrollFor': '{month} జీతాలు',
        'shiftsAdvance': '{days} షిఫ్ట్‌లు · అడ్వాన్స్ {adv}',
        'statePaid': 'ఇచ్చారు',
        'statePending': 'ఇవ్వాలి',
        'stateDraft': 'డ్రాఫ్ట్',
        'salaryNote': 'రోజులు మేనేజర్ రాసిన హాజరు నుంచి వస్తాయి. జీతాలు ఇచ్చిన నెలలో లాభ నష్టాల్లో “జీతాలు” కింద చేరతాయి.',
        'logAdvance': 'అడ్వాన్స్ రాయండి',
        'markPaid': 'జీతాలు ఇచ్చినట్టు గుర్తించండి',
        'logExpense': 'ఇంకో చెల్లించిన డబ్బు రాయండి',
        'noPayroll': '{month} జీతాల లెక్క ఇంకా లేదు',
        'noPayrollBody': 'హాజరు, రోజు కూలీ నుంచి జీతం లెక్కించడానికి దీన్ని తయారు చేయండి.',
        'createPayroll': '{month} జీతాల లెక్క తయారు చేయండి',
        'markPaidTitle': '{n} జీతాలు ఇచ్చినట్టు గుర్తించాలా?',
        'markPaidBody': 'జీతాల లెక్క ఖరారవుతుంది. ఇంకా ఇవ్వని ప్రతి జీతం {mode} ద్వారా ఇచ్చినట్టు రాస్తుంది. జీతాలు ఖర్చుల్లో చేరతాయి.',
        'paidDone': 'జీతాలు ఇచ్చినట్టు గుర్తించాం',
        'fStaff': 'సిబ్బంది',
        'fAmount': 'మొత్తం (₹)',
        'fMode': 'ఎలా ఇచ్చారు',
        'advanceSaved': 'అడ్వాన్స్ రాశాం — తర్వాతి జీతంలో తగ్గిస్తాం',
        'pm_CASH': 'క్యాష్',
        'pm_UPI': 'UPI',
        'pm_BANK': 'బ్యాంక్ ట్రాన్స్‌ఫర్',
        'pm_CARD': 'కార్డ్',
        'pm_CHEQUE': 'చెక్',
        'pm_OTHER': 'ఇతర',
        // expense types
        'expenseTypesTitle': 'చెల్లించిన డబ్బు రకాలు',
        'expenseTypesSub': 'లిస్ట్ · {n} రకాలు',
        'expenseTypesIntro': 'చెల్లించిన డబ్బు రాసేటప్పుడు ఈ రకాల్లో ఒకటి ఎంచుకుంటారు. ఎవరు రాయవచ్చో మార్చడానికి రకాన్ని నొక్కండి — {visible}.',
        'visibleCount': '{t} రకాల్లో {v} మేనేజర్‌లకు కనిపిస్తాయి',
        'mtd': 'ఈ నెల {v}',
        'managersCanLog': 'మేనేజర్‌లు రాయవచ్చు',
        'ownerOnly': 'యజమాని మాత్రమే',
        'inactiveType': 'వాడుకలో లేదు',
        'expenseTypesNote': 'ప్రతి రకం లాభ నష్టాల్లో ఒక వరుసకు చేరుతుంది. “యజమాని మాత్రమే” రకాలు — అద్దె, జీతాలు, లైసెన్స్‌లు — మేనేజర్ చెల్లించిన డబ్బు రాసే చోట కనిపించవు. అందుకే రోజువారీ బజారు ఖర్చు, నెలవారీ ఖర్చులు వేరుగా ఉంటాయి.',
        'addExpenseType': 'రకం జోడించండి',
        'typeName': 'రకం పేరు',
        'typeSaved': 'రకం సేవ్ అయింది',
        'noExpenseTypes': 'ఇంకా రకాలు లేవు',
    },
);
